/**
 * Simple IndexedDB wrapper for persisting template state
 */

const DB_NAME = 'FigmaMakeDB_v2';
const DB_VERSION = 1;
const STORE_NAME = 'templates';

export interface SavedState {
  id: string;
  originTemplateId?: string;
  version?: string;
  width?: number;
  height?: number;
  canvasDimensions?: { width: number; height: number };
  name: string;
  previewUrl: string;
  dynamicLayers: any[];
  layerPositions: Record<string, any>;
  textContents: Record<string, string>;
  textStyles: Record<string, any>;
  backgroundSettings: Record<string, any>;
  imageSettings: Record<string, any>;
  videoSettings: Record<string, any>;
  shapeSettings: Record<string, any>;
  scrollerSettings: Record<string, any>;
  widgetSettings?: Record<string, any>;
  pageName: string;
  templateSettings: any;
  pages?: any[]; // Array of PageData
  currentPageIndex?: number;
  lastModified: number;
}

export const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
};

export const saveState = async (state: SavedState): Promise<void> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(state);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
};

export const getState = async (id: string): Promise<SavedState | null> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || null);
  });
};

export const deleteState = async (id: string): Promise<void> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
};

export const getAllStates = async (): Promise<SavedState[]> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || []);
  });
};

